/**
 * Lógica pura de la selección de asesores del Resumen CX (SS-537).
 *
 * Modelo: la UI trabaja con los asesores TILDADOS (todos por defecto). Hacia el backend `[]` sigue
 * significando «sin filtro», así que «todos tildados» no manda lista (la petición no crece con la
 * cantidad de asesores). «Ninguno tildado» es un estado propio: sin resultados.
 */

/** Largo máximo del texto de asesores en el desplegable antes de recortar con «...» */
const PLACEHOLDER_MAX_LENGTH = 15;

const containsAll = (selected, available) => available.every((agent) => selected.includes(agent));

/**
 * Reconcilia la selección cuando cambia la lista de asesores disponibles (primera carga, cambio de
 * supervisor o de rango).
 * - Sin lista previa (primera carga) o con todos tildados: tilda a todos los nuevos.
 * - Con selección parcial (o ninguna): conserva los que siguen disponibles; no suma los nuevos.
 * @param {string[]} prevSelected - Asesores tildados antes del cambio
 * @param {string[]} prevAvailable - Asesores disponibles antes del cambio
 * @param {string[]} nextAvailable - Asesores disponibles ahora
 * @returns {string[]} Selección nueva
 */
export function reconcileSelection(prevSelected, prevAvailable, nextAvailable) {
  const selected = prevSelected || [];
  const previous = prevAvailable || [];
  const next = nextAvailable || [];

  if (previous.length === 0 || containsAll(selected, previous)) {
    return [...next];
  }
  return next.filter((agent) => selected.includes(agent));
}

/**
 * Convierte la selección de la UI en el filtro que se manda al backend.
 * @param {string[]} selected - Asesores tildados
 * @param {string[]} available - Asesores disponibles
 * @returns {{agents: string[], none: boolean}} `agents` vacío = sin filtro; `none` = nada tildado
 */
export function buildAgentFilter(selected, available) {
  const options = available || [];
  if (options.length === 0) {
    return { agents: [], none: false };
  }

  const ticked = (selected || []).filter((agent) => options.includes(agent));
  if (ticked.length === 0) {
    return { agents: [], none: true };
  }
  if (ticked.length === options.length) {
    return { agents: [], none: false };
  }
  return { agents: ticked, none: false };
}

/**
 * Texto que muestra el desplegable de asesores.
 * @param {string[]} selected - Asesores tildados
 * @param {string[]} available - Asesores disponibles
 * @param {Function} t - Traducción
 * @returns {string}
 */
export function getAgentsPlaceholderText(selected, available, t) {
  const { agents, none } = buildAgentFilter(selected, available);
  if (none) {
    return t('insights.noAgentsSelected');
  }
  if (agents.length === 0) {
    return t('insights.all');
  }

  const names = agents.join(', ');
  return names.length > PLACEHOLDER_MAX_LENGTH ? `${names.slice(0, PLACEHOLDER_MAX_LENGTH)}...` : names;
}
